import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  ChevronRight, Gavel, Users, FileText, CheckCircle,
  Calendar, AlertTriangle, Send
} from 'lucide-react';

export default function InscriptionVacation() {
  const { vacationSlug } = useParams();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [vacation, setVacation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  
  const [formData, setFormData] = useState({
    entreprise: '',
    siret: '',
    pays: 'France',
    contact_nom: '',
    contact_email: '',
    contact_tel: '',
    financement: 'non_precise',
    delai_paiement_annonce: '',
    projet: '',
    acceptation_conditions: false
  });

  // Pre-fill form with user data when available
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        contact_nom: prev.contact_nom || user.name || '',
        contact_email: prev.contact_email || user.email || '',
        entreprise: prev.entreprise || user.company || ''
      }));
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      navigate(`/encheres/connexion?redirect=/encheres/${vacationSlug}/inscription`);
      return;
    }
    fetchVacation();
  }, [vacationSlug, user]);

  // Check for existing inscription when vacation is loaded
  useEffect(() => {
    if (user && vacation) {
      checkExistingInscription();
    }
  }, [user, vacation]);

  const fetchVacation = async () => {
    try {
      const response = await axios.get(`${API}/encheres/vacations/${vacationSlug}`);
      setVacation(response.data);
    } catch (error) {
      console.error('Error fetching vacation:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkExistingInscription = async () => {
    if (!user || !vacation) return;
    try {
      const response = await axios.get(`${API}/encheres/mes-inscriptions?acheteur_id=${user.id}`);
      const existing = (response.data || []).find(i => i.vacation_id === vacation.id);
      if (existing) {
        setAlreadyRegistered(true);
        toast.info('Vous êtes déjà inscrit à cette vente');
      }
    } catch (error) {
      console.error('Error checking inscription:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.acceptation_conditions) {
      toast.error('Vous devez accepter les conditions de vente');
      return;
    }

    if (!formData.entreprise || !formData.siret || !formData.contact_email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/encheres/inscriptions?acheteur_id=${user.id}`, {
        ...formData,
        vacation_id: vacation.id
      });
      
      toast.success('Votre inscription a été enregistrée');
      navigate('/encheres/mon-compte');
    } catch (error) {
      let msg = 'Erreur lors de l\'inscription';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors
        msg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        msg = detail.msg || detail.message || JSON.stringify(detail);
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#F0A22E]"></div>
      </div>
    );
  }

  if (!vacation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Gavel className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h1 className="text-2xl font-bold text-gray-700 mb-2">Vente non trouvée</h1>
          <Link to="/encheres">
            <Button>Retour aux enchères</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (vacation.statut !== 'annoncee') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-700 mb-2">Inscriptions closes</h1>
          <p className="text-gray-500 mb-4">
            Les inscriptions pour cette vente ne sont plus ouvertes.
          </p>
          <Link to={`/encheres/${vacationSlug}`}>
            <Button>Voir la vente</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Inscription | {vacation.titre_fr} | GEOTS Enchères</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header */}
      <div className="bg-[#0A2540] text-white">
        <div className="container mx-auto px-4 py-8">
          <nav className="flex items-center gap-2 text-sm text-gray-300 mb-4">
            <Link to="/" className="hover:text-white">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/encheres" className="hover:text-white">Enchères</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to={`/encheres/${vacationSlug}`} className="hover:text-white truncate max-w-[150px]">
              {vacation.titre_fr}
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white font-medium">Inscription</span>
          </nav>

          <div className="flex items-center gap-4">
            <div className="bg-green-500/20 p-3 rounded-lg">
              <Users className="w-8 h-8 text-green-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">S&apos;inscrire à la vente</h1>
              <p className="text-gray-300">{vacation.titre_fr}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Formulaire d&apos;inscription</CardTitle>
                <p className="text-sm text-gray-500">
                  Remplissez ce formulaire pour participer aux enchères de cette vente.
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Company */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#0A2540] border-b pb-2">Votre entreprise</h3>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="entreprise">Raison sociale *</Label>
                        <Input
                          id="entreprise"
                          value={formData.entreprise}
                          onChange={(e) => handleChange('entreprise', e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="siret">SIRET *</Label>
                        <Input
                          id="siret"
                          value={formData.siret}
                          onChange={(e) => handleChange('siret', e.target.value)}
                          placeholder="123 456 789 00012"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="pays">Pays</Label>
                      <Select 
                        value={formData.pays} 
                        onValueChange={(v) => handleChange('pays', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="France">France</SelectItem>
                          <SelectItem value="Belgique">Belgique</SelectItem>
                          <SelectItem value="Suisse">Suisse</SelectItem>
                          <SelectItem value="Luxembourg">Luxembourg</SelectItem>
                          <SelectItem value="Autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Contact */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#0A2540] border-b pb-2">Contact</h3>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contact_nom">Nom complet *</Label>
                        <Input
                          id="contact_nom"
                          value={formData.contact_nom}
                          onChange={(e) => handleChange('contact_nom', e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_tel">Téléphone *</Label>
                        <Input
                          id="contact_tel"
                          type="tel"
                          value={formData.contact_tel}
                          onChange={(e) => handleChange('contact_tel', e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="contact_email">Email *</Label>
                      <Input
                        id="contact_email"
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => handleChange('contact_email', e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {/* Project */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#0A2540] border-b pb-2">Votre projet</h3>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="financement">Mode de financement</Label>
                        <Select 
                          value={formData.financement} 
                          onValueChange={(v) => handleChange('financement', v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fonds_propres">Fonds propres</SelectItem>
                            <SelectItem value="accord_bancaire">Accord bancaire obtenu</SelectItem>
                            <SelectItem value="a_instruire">Financement à instruire</SelectItem>
                            <SelectItem value="non_precise">Non précisé</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="delai_paiement">Délai de paiement envisagé</Label>
                        <Input
                          id="delai_paiement"
                          value={formData.delai_paiement_annonce}
                          onChange={(e) => handleChange('delai_paiement_annonce', e.target.value)}
                          placeholder="Ex: Comptant, 30 jours..."
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="projet">Description de votre projet (optionnel)</Label>
                      <Textarea
                        id="projet"
                        value={formData.projet}
                        onChange={(e) => handleChange('projet', e.target.value)}
                        placeholder="Décrivez brièvement votre projet d'acquisition..."
                        rows={3}
                      />
                    </div>
                  </div>

                  {/* Conditions */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Checkbox 
                        id="conditions" 
                        checked={formData.acceptation_conditions}
                        onCheckedChange={(checked) => handleChange('acceptation_conditions', checked)}
                      />
                      <Label htmlFor="conditions" className="text-sm leading-relaxed">
                        J&apos;accepte les{' '}
                        <Link to="/encheres/conditions" className="text-[#F0A22E] hover:underline" target="_blank">
                          conditions générales de vente par courtage aux enchères
                        </Link>{' '}
                        et je m&apos;engage à fournir les pièces justificatives demandées.
                      </Label>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#F0A22E] hover:bg-[#d89426] text-lg py-6"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <span className="animate-pulse">Envoi en cours...</span>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Valider mon inscription
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Vacation Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">À propos de cette vente</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-[#F0A22E]" />
                  <span>Clôture des inscriptions</span>
                </div>
                <p className="font-semibold text-[#0A2540]">
                  {formatDate(vacation.date_cloture_inscriptions)}
                </p>
                
                <div className="flex items-center gap-2 text-sm mt-4">
                  <Gavel className="w-4 h-4 text-[#F0A22E]" />
                  <span>Début des enchères</span>
                </div>
                <p className="font-semibold text-[#0A2540]">
                  {formatDate(vacation.date_ouverture_vente)}
                </p>

                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-500">
                    <strong>{vacation.nb_lots}</strong> lot{vacation.nb_lots > 1 ? 's' : ''} proposé{vacation.nb_lots > 1 ? 's' : ''}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Documents to provide */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-6">
                <FileText className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="font-bold text-blue-800 mb-2">Pièces à fournir</h3>
                <p className="text-sm text-blue-700 mb-3">
                  Après votre inscription, envoyez ces documents par email :
                </p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Extrait Kbis de moins de 3 mois</li>
                  <li>• Pièce d&apos;identité du dirigeant</li>
                  <li>• Justificatif de financement</li>
                </ul>
                <p className="text-xs text-blue-600 mt-3">
                  Envoyez à : contact@geots.consulting
                </p>
              </CardContent>
            </Card>

            {/* Steps */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Prochaines étapes</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-3 text-sm">
                  <li className="flex gap-3">
                    <span className="bg-[#F0A22E] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                    <span>Validez votre inscription</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="bg-gray-200 text-gray-600 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                    <span>Envoyez vos pièces justificatives</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="bg-gray-200 text-gray-600 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                    <span>Recevez votre validation</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="bg-gray-200 text-gray-600 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                    <span>Enchérissez sur les lots</span>
                  </li>
                </ol>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
